import React, { useState } from "react";
import * as XLSX from "xlsx";
import SidbarComponent from "./SidbarComponent";
import TopBarComponent from "./TopBarComponent";
import { FooterComponents } from "./FooterComponent";
import {
  Upload,
  CheckCircle,
  XCircle,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { getToken } from "../utils/auth";

export default function HliUploadStudents() {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [results, setResults] = useState({ success: [], failed: [] });
  const [error, setError] = useState("");

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const ext = selectedFile.name.split(".").pop().toLowerCase();
      if (["xlsx", "xls"].includes(ext)) {
        setFile(selectedFile);
        setError("");
        setResults({ success: [], failed: [] });
      } else {
        setError("Please upload a valid Excel file (.xlsx or .xls)");
        setFile(null);
      }
    }
  };

  const parseExcelFile = (f) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const wb = XLSX.read(data, { type: "array" });
          const sheet = wb.Sheets[wb.SheetNames[0]];
          resolve(XLSX.utils.sheet_to_json(sheet));
        } catch {
          reject(new Error("Failed to parse Excel file"));
        }
      };
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsArrayBuffer(f);
    });

  const sendToBackend = async (record) => {
    const res = await fetch(
      process.env.REACT_APP_BASE_URL + "hec/busaryApplication/saveApplicationByMyInstitution",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + getToken(),
        },
        body: JSON.stringify(record),
      }
    );
    if (!res.ok) {
      const msg = await res.text();
      throw new Error(msg || `HTTP ${res.status}`);
    }
    return true;
  };

  const handleUpload = async () => {
    if (!file) { setError("Please select a file first"); return; }
    setUploading(true);
    setError("");
    setResults({ success: [], failed: [] });
    setProgress({ current: 0, total: 0 });

    try {
      const parsedData = await parseExcelFile(file);
      if (!parsedData || parsedData.length === 0) throw new Error("No data found in Excel file");

      setProgress({ current: 0, total: parsedData.length });
      const successRecords = [];
      const failedRecords = [];

      for (let i = 0; i < parsedData.length; i++) {
        const record = parsedData[i];
        try {
          await sendToBackend(record);
          successRecords.push({ index: i + 1, data: record });
        } catch (err) {
          failedRecords.push({ index: i + 1, data: record, error: err.message });
        }
        setProgress({ current: i + 1, total: parsedData.length });
      }
      setResults({ success: successRecords, failed: failedRecords });
      if (failedRecords.length > 0 && successRecords.length === 0) setError("All records failed to upload");
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const resetUpload = () => {
    setFile(null);
    setResults({ success: [], failed: [] });
    setError("");
    setProgress({ current: 0, total: 0 });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-cyan-50 flex flex-col">
      <TopBarComponent />
      <aside
        id="logo-sidebar"
        className="app-legacy-aside transition-transform -translate-x-full sm:translate-x-0"
        aria-label="Sidebar"
      >
        <SidbarComponent />
      </aside>
      <div className="p-6 sm:ml-64 flex-1 flex flex-col">
        <div className="mb-8">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8">
            <div className="flex items-center space-x-4">
              <div className="w-14 h-14 bg-gradient-to-r from-cyan-600 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
                <Upload className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Upload Students</h1>
                <p className="text-gray-500 mt-1">Upload bursary application student lists for your institution via Excel</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-8 max-w-3xl">
          <p className="text-gray-600 mb-6 text-sm">
            Please prepare your Excel file in the required format before uploading.
            Only students belonging to your institution's colleges will be accepted.
          </p>

          {/* File Input */}
          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700 mb-2">Select Excel File</label>
            <div className="flex items-center gap-4">
              <label className="flex-1 flex items-center justify-center px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-cyan-500 transition-colors">
                <Upload className="w-5 h-5 mr-2 text-gray-400" />
                <span className="text-sm text-gray-600">
                  {file ? file.name : "Choose Excel file (.xlsx, .xls)"}
                </span>
                <input type="file" accept=".xlsx,.xls" onChange={handleFileChange} className="hidden" disabled={uploading} />
              </label>
              {file && !uploading && (
                <button onClick={resetUpload} className="px-4 py-3 text-sm text-gray-500 hover:text-gray-700">
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="mb-5 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
              <AlertCircle className="w-5 h-5 text-red-500 mr-3 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Upload Button */}
          <button
            onClick={handleUpload}
            disabled={!file || uploading}
            className="w-full px-6 py-3 bg-cyan-600 text-white rounded-lg font-medium hover:bg-cyan-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
          >
            {uploading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Processing... ({progress.current}/{progress.total})
              </>
            ) : (
              <>
                <Upload className="w-5 h-5 mr-2" />
                Upload and Process
              </>
            )}
          </button>

          {/* Progress Bar */}
          {uploading && progress.total > 0 && (
            <div className="mt-5">
              <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                <div
                  className="bg-cyan-600 h-full transition-all duration-300"
                  style={{ width: `${(progress.current / progress.total) * 100}%` }}
                />
              </div>
              <p className="text-sm text-gray-600 mt-2 text-center">
                {Math.round((progress.current / progress.total) * 100)}% Complete
              </p>
            </div>
          )}

          {/* Results */}
          {(results.success.length > 0 || results.failed.length > 0) && (
            <div className="mt-8 space-y-4">
              <h2 className="text-lg font-semibold text-gray-800">Upload Results</h2>

              {results.success.length > 0 && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                  <p className="font-medium text-green-800">
                    Successfully uploaded {results.success.length} record(s)
                  </p>
                </div>
              )}

              {results.failed.length > 0 && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-start">
                    <XCircle className="w-5 h-5 text-red-500 mr-3 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-medium text-red-800 mb-2">
                        Failed to upload {results.failed.length} record(s)
                      </p>
                      <div className="max-h-48 overflow-y-auto space-y-2">
                        {results.failed.map((fail, idx) => (
                          <div key={idx} className="text-sm text-red-700 bg-red-100 p-2 rounded">
                            <p className="font-medium">Row {fail.index}: {fail.error}</p>
                            <p className="text-xs mt-1 text-red-600">
                              {fail.data.firstName} {fail.data.lastName} — {fail.data.registrationNumber}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <FooterComponents />
    </div>
  );
}

